import { TypesenseEventDocument } from '@types';

/**
 * Returns the appropriate button text based on the event's ticket availability and additional booking parameters.
 *
 * This function determines the button text to display for booking or purchasing tickets
 * based on the availability status and provided options for booking (such as a Tonic ID, custom booking URL, or Design My Night type).
 *
 * @param {Object} params - The parameters object.
 * @param {DateAndTime["ticketAvailability"]} params.availability - The availability status of the event. Possible values:
 *   - "available": Tickets are available for purchase.
 *   - "sold-out": Tickets are sold out.
 *   - "cancelled": The event has been cancelled.
 * @param {string} [params.tonicId] - Optional. The Tonic ID for booking tickets. If provided, the button will say "Buy Tickets".
 * @param {string} [params.customBookingUrl] - Optional. A custom URL for booking tickets. If provided, the button will say "Buy Tickets".
 * @param {string} [params.designMyNightType] - Optional. A Design My Night type for booking. If provided, the button will say "Book Now".
 *
 * @returns {string} The appropriate button text based on the availability and provided booking parameters. Possible values:
 *   - "Sold Out" if the event is sold out or if there is no booking method available.
 *   - "Buy Tickets" if a Tonic ID or custom booking URL is provided.
 *   - "Book Now" if a Design My Night type is provided.
 *   - "Cancelled" if the event is cancelled.
 */
export function getBookButtonText(event: TypesenseEventDocument) {
  const availability = event.dates_ticketAvailability;
  const tonicId = event.tonic?.active ? event.tonic.id : undefined;
  // const designMyNightType = event.designMyNight?.active ? event.designMyNight.id : undefined;
  const designMyNightType = event.designMyNight?.active;
  const bookingUrl = event.bookingUrl;

  const available =
    !tonicId && !designMyNightType && !bookingUrl ? 'Sold Out' : tonicId || bookingUrl ? 'Buy Tickets' : 'Book Now';

  switch (availability) {
    case 'available':
      return available;
    case 'sold-out':
      return 'Sold Out';
    case 'cancelled':
      return 'Cancelled';
    default:
      return available;
  }
}
